import { Crosshair, Leaf, Users, Award } from "lucide-react";
import Reveal from "@/components/Reveal";
import { useLanguage } from "@/i18n/LanguageContext";

const VALUE_ICONS = [Crosshair, Leaf, Users];

const TEAM_PHOTOS = {
  "mads-kjaeras": "/team/mads.png",
  "marek-piotrowicz": "/team/marek.png",
};

const About = () => {
  const { t } = useLanguage();
  const values = t("about.values");
  const team = t("about.team");
  const models = t("about.models");

  return (
    <section id="om-oss" className="scroll-mt-20 border-t border-slate-200 bg-slate-50 py-20 sm:py-28" data-testid="about-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-8">
        <div className="grid gap-14 lg:grid-cols-2">
          <div>
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-sky-700" data-testid="about-badge">
              {t("about.badge")}
            </p>
            <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl" data-testid="about-title">
              {t("about.title")}
            </h2>
            <p className="mt-5 text-sm leading-relaxed text-slate-600 sm:text-base">{t("about.p1")}</p>
            <p className="mt-4 text-sm leading-relaxed text-slate-600 sm:text-base">{t("about.p2")}</p>

            <div className="mt-8 flex items-start gap-4 rounded-lg border border-sky-200 bg-sky-50 p-6" data-testid="about-pro-banner">
              <Award size={26} className="mt-0.5 shrink-0 text-sky-700" />
              <div>
                <h3 className="font-display text-base font-bold text-slate-900">{t("about.proTitle")}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{t("about.proText")}</p>
              </div>
            </div>
          </div>

          <div className="space-y-5">
            {values.map((v, i) => {
              const Icon = VALUE_ICONS[i] || Crosshair;
              return (
                <div key={v.title} className="flex gap-5 rounded-lg border border-slate-200 bg-white p-6 shadow-sm" data-testid={`about-value-${i}`}>
                  <div className="h-fit rounded-md border border-sky-100 bg-sky-50 p-3 text-sky-700">
                    <Icon size={20} />
                  </div>
                  <div>
                    <h3 className="font-display text-base font-bold text-slate-900">{v.title}</h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{v.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="mt-20">
          <Reveal>
            <h3 className="font-display text-xl font-bold text-slate-900 sm:text-2xl" data-testid="team-title">
              {t("about.teamTitle")}
            </h3>
            <p className="mt-2 max-w-2xl text-sm text-slate-600 sm:text-base">{t("about.teamIntro")}</p>
          </Reveal>
          <div className="mt-8 grid gap-5 sm:grid-cols-2">
            {team.map((p) => (
              <Reveal key={p.id} delay={0.08}>
                <div className="flex h-full gap-6 rounded-lg border border-slate-200 bg-white p-7 shadow-sm" data-testid={`team-card-${p.id}`}>
                  <img
                    src={TEAM_PHOTOS[p.id]}
                    alt={`Portrett av ${p.name}`}
                    className="h-20 w-20 shrink-0 rounded-full border-2 border-sky-100 object-cover"
                    data-testid={`team-avatar-${p.id}`}
                  />
                  <div>
                    <h4 className="font-display text-lg font-bold text-slate-900">{p.name}</h4>
                    <p className="mt-0.5 font-mono text-xs font-semibold uppercase tracking-widest text-sky-700">{p.role}</p>
                    <p className="mt-3 text-sm leading-relaxed text-slate-600">{p.bio}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        <div className="mt-20">
          <h3 className="font-display text-xl font-bold text-slate-900 sm:text-2xl" data-testid="collab-title">
            {t("about.collabTitle")}
          </h3>
          <p className="mt-2 max-w-2xl text-sm text-slate-600 sm:text-base">{t("about.collabIntro")}</p>
          <div className="mt-8 grid gap-5 md:grid-cols-3">
            {models.map((m) => (
              <div
                key={m.letter}
                className={`relative rounded-lg border p-7 ${
                  m.recommended ? "border-sky-600/60 bg-white shadow-md" : "border-slate-200 bg-white"
                }`}
                data-testid={`collab-model-${m.letter.toLowerCase()}`}
              >
                {m.recommended && (
                  <span className="absolute -top-3 left-6 rounded-full bg-sky-600 px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-widest text-white">
                    {t("about.recommended")}
                  </span>
                )}
                <span className="font-display text-3xl font-bold text-sky-700">{m.letter}</span>
                <h4 className="mt-3 font-display text-lg font-bold text-slate-900">{m.name}</h4>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{m.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
